import PopupDialog from '@/components/popupDialog/PopupDialog'
import Row from '@/components/table/Row'
import Table from '@/components/table/Table'
import {
  useLazyAddUsersToSchemaQuery,
  useLazyGetSchemaQuery,
  useLazyRemoveUserFromSchemaQuery,
} from '@/store/apis/schemas'
import { IUserObject, useLazyGetUsersQuery } from '@/store/apis/users'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import DangerousIcon from '@mui/icons-material/Dangerous'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

interface IState {
  selectedPopup?: 'REMOVE_USER'
  addSchemaUser?: IUserObject
  removeUser?: IUserObject
  rowHoverId?: number
  processingId?: number // ADDED: Track which specific user is being updated
}

const SchemaUsers: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()

  const [getSchemaQuery, { data: schemaData, isLoading: isLoadingSchema }] =
    useLazyGetSchemaQuery()

  const [addUsersToSchema, { isFetching: isAddingUser }] =
    useLazyAddUsersToSchemaQuery()

  const [
    getUsersQuery,
    { data: usersData, isLoading: isLoadingUsers, isFetching: isFetchingUsers },
  ] = useLazyGetUsersQuery()

  const [removeUserFromSchema, { isFetching: isRemovingUser }] =
    useLazyRemoveUserFromSchemaQuery()

  const [state, setState] = React.useState<IState>({})

  React.useEffect(() => {
    getUsersQuery({})
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: Number(urlParams.schemaId) })
    }
  }, [getUsersQuery, getSchemaQuery, urlParams])

  // Helper to refresh all relevant data
  const refreshData = async () => {
    if (urlParams?.schemaId) {
      // We must await both to ensure the UI recalculates foundIndex correctly
      await Promise.all([
        getSchemaQuery({ id: Number(urlParams.schemaId) }),
        getUsersQuery({}), // FIX: Refetch the users list so user.schemas is updated
      ])
    }
  }

  const handleAddUser = async (id: number) => {
    if (schemaData && !isRemovingUser && !isAddingUser) {
      setState((prev) => ({ ...prev, processingId: id }))
      await addUsersToSchema({
        id: schemaData.schema.schema.id,
        userIds: [id],
      })
      await refreshData()
      setState((prev) => ({
        ...prev,
        addSchemaUser: undefined,
        selectedPopup: undefined,
        processingId: undefined,
      }))
    }
  }

  const handleUserRemove = async (id: number): Promise<void> => {
    if (schemaData && !isRemovingUser && !isAddingUser) {
      setState((prev) => ({ ...prev, processingId: id }))
      await removeUserFromSchema({
        id: schemaData.schema.schema.id,
        userId: id,
      })
      await refreshData()
      setState((prev) => ({
        ...prev,
        removeUser: undefined,
        selectedPopup: undefined,
        processingId: undefined,
      }))
    }
  }

  const { selectedPopup, removeUser, processingId } = state
  const { items = [] } = usersData ? usersData : {}

  return (
    <>
      <Table
        headerLabels={[
          t('headers.firstName'),
          t('headers.sureName'),
          t('headers.filledInTimeslots'),
          t('headers.inOut'),
        ]}
        noButtons={true}
        isLoading={isLoadingUsers || isLoadingSchema}
      >
        {items.map((user, index) => {
          // Check if this specific user is currently being updated
          const isBusy =
            processingId === user.user.id || isAddingUser || isRemovingUser

          let foundIndex = -1
          if (user.schemas.length && schemaData) {
            foundIndex = schemaData.users.findIndex((schemaUser) => {
              const isUserInThisSchema = user.schemas.some(
                (userSchema) => userSchema.id === schemaData.schema.schema.id,
              )
              return isUserInThisSchema && schemaUser.id === user.user.id
            })
          }

          return (
            <Row
              key={user.user.id} // Use ID for better React reconciliation
              navigateTo={
                schemaData && state.rowHoverId !== index
                  ? `/users/${user.user.id}/schemas/${schemaData.schema.schema.id}`
                  : undefined
              }
              columns={{
                normal: [
                  user.user.firstName,
                  user.user.lastName,
                  foundIndex > -1 &&
                  schemaData?.users[foundIndex].filledInTimeslots ? (
                    <Grid item xs display="flex" justifyContent="left">
                      <b style={{ color: 'green' }}>{t('yes')}</b>
                      <CheckBoxIcon style={{ color: 'green' }} />
                    </Grid>
                  ) : (
                    <Grid item xs display="flex" justifyContent="left">
                      <b style={{ color: 'red' }}>{t('no')}</b>
                      <DangerousIcon style={{ color: 'red' }} />
                    </Grid>
                  ),
                  <Switch
                    color="primary"
                    checked={foundIndex > -1}
                    // Disable if global fetching is active OR this specific row is processing
                    disabled={isFetchingUsers || isBusy}
                    onMouseEnter={() =>
                      setState({ ...state, rowHoverId: index })
                    }
                    onMouseLeave={() =>
                      setState({ ...state, rowHoverId: undefined })
                    }
                    onClick={(event) => {
                      event.stopPropagation()
                      if (isBusy) return

                      if (foundIndex > -1) {
                        setState({
                          ...state,
                          selectedPopup: 'REMOVE_USER',
                          removeUser: user.user,
                        })
                      } else {
                        handleAddUser(user.user.id)
                      }
                    }}
                  />,
                ],
              }}
            />
          )
        })}
      </Table>
      <PopupDialog
        isOpen={selectedPopup === 'REMOVE_USER'}
        title={
          <>
            {t('popups.removeUserFromSchemaTitle')}:{' '}
            <b>{removeUser?.firstName}</b>
          </>
        }
        content={t('popups.removeUserFromSchemaDescription')}
        isLoading={isRemovingUser}
        onClose={() =>
          setState({
            ...state,
            selectedPopup: undefined,
            processingId: undefined,
          })
        }
        onConfirm={() =>
          removeUser?.id ? handleUserRemove(removeUser.id) : null
        }
      />
    </>
  )
}

export default SchemaUsers
